(() => {
  const revealObservers = new WeakMap();
  const initializeReveals = home => {
    // Everything stays visible before, during, and after enhancement.
    if (typeof window.IntersectionObserver !== 'function' ||
        typeof window.matchMedia !== 'function' ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    try {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            entry.target.classList.add('gos-reveal-in');
          }
        });
      }, { threshold: 0.08 });
      revealObservers.set(home, observer);
      home.querySelectorAll('[data-gos-reveal]').forEach(section => {
        // Do not animate content already in the initial viewport.
        if (section.getBoundingClientRect().top >= window.innerHeight) observer.observe(section);
      });
    } catch (_) {
      revealObservers.get(home)?.disconnect();
      revealObservers.delete(home);
    }
  };
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-gos-home]').forEach(home => {
      if (home.dataset.ready) return;
      home.dataset.ready = 'true';
      const grid = home.querySelector('[data-gos-grid]');
      const cards = [...grid.querySelectorAll('[data-gos-card]')];
      const search = home.querySelector('[data-gos-search]');
      const sort = home.querySelector('[data-gos-sort]');
      const reset = home.querySelector('[data-gos-reset]');
      const empty = home.querySelector('[data-gos-empty]');
      const count = home.querySelector('[data-gos-count]');
      const recommendations = home.querySelector('[data-gos-recommendations]');
      const needPanels = recommendations ? [...recommendations.querySelectorAll('[data-gos-need-panel]')] : [];
      const needStatus = home.querySelector('[data-gos-need-status]');
      const needTargets = new Map();
      home.querySelectorAll('[data-gos-need-target]').forEach(link => {
        const panel = needPanels.find(candidate => candidate.id === link.dataset.gosNeedTarget);
        if (panel) needTargets.set(link, panel);
      });
      const isPlainClick = event => !event.defaultPrevented && event.button === 0 &&
        !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      const activateNeed = link => {
        const panel = needTargets.get(link);
        const closing = !recommendations.hidden && !panel.hidden;
        needPanels.forEach(candidate => { candidate.hidden = closing || candidate !== panel; });
        needTargets.forEach((_, control) => control.setAttribute('aria-pressed', String(!closing && control === link)));
        recommendations.hidden = closing;
        if (needStatus) {
          needStatus.textContent = (closing ? recommendations.dataset.gosClosedAnnouncement : panel.dataset.gosAnnouncement) || '';
        }
        if (!closing && typeof panel.scrollIntoView === 'function') {
          const heading = panel.querySelector('h3') || panel;
          const bounds = heading.getBoundingClientRect();
          const headerBottom = home.querySelector('.site-header')?.getBoundingClientRect().bottom || 0;
          if (bounds.top > window.innerHeight - 100 || bounds.bottom < headerBottom) {
            const reduce = typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            panel.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
          }
        }
      };
      let tab = 'all';
      let opener;
      const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const update = () => {
        const query = normalize(search.value.trim());
        let total = 0;
        const ordered = cards.map((card, index) => ({ card, index }));
        if (sort.value !== 'featured') ordered.sort((a, b) => (Number(a.card.dataset.price) - Number(b.card.dataset.price)) * (sort.value === 'high' ? -1 : 1) || a.index - b.index);
        ordered.forEach(({ card }) => {
          card.hidden = !(tab === 'all' || card.dataset.groups.split(' ').includes(tab)) || !normalize(card.dataset.search).includes(query);
          if (!card.hidden) total++;
          grid.append(card);
        });
        count.textContent = `${total} ${total === 1 ? home.dataset.singular : home.dataset.plural}`;
        empty.hidden = total !== 0;
        grid.hidden = total === 0;
        reset.hidden = !query && tab === 'all';
        home.querySelectorAll('[data-gos-tab]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.gosTab === tab)));
      };
      home.querySelectorAll('[data-gos-enhance]').forEach(node => { node.hidden = false; });
      search.addEventListener('input', update);
      sort.addEventListener('change', update);
      reset.addEventListener('click', () => { search.value = ''; tab = 'all'; update(); });
      home.addEventListener('click', event => {
        const needLink = event.target.closest('[data-gos-need-target]');
        if (needTargets.has(needLink) && isPlainClick(event) &&
            !needLink.hasAttribute('download') && (!needLink.target || needLink.target === '_self')) {
          event.preventDefault();
          activateNeed(needLink);
          return;
        }
        const tabButton = event.target.closest('[data-gos-tab]');
        if (tabButton) { tab = tabButton.dataset.gosTab; update(); }
        const trigger = event.target.closest('[data-gos-quick]');
        if (trigger && isPlainClick(event) &&
            !trigger.hasAttribute('download') && (!trigger.target || trigger.target === '_self')) {
          const dialog = document.getElementById(trigger.dataset.gosQuick);
          if (dialog && typeof dialog.showModal === 'function') {
            event.preventDefault();
            opener = trigger;
            dialog.showModal();
            document.body.classList.add('dialog-open');
            dialog.querySelector('[data-gos-close]')?.focus();
          }
        }
        if (event.target.closest('[data-gos-close]')) event.target.closest('dialog')?.close();
        const menuLink = event.target.closest('.gos-menu a');
        if (menuLink) menuLink.closest('details').open = false;
        const directory = event.target.closest('a[href="#gos-brand-directory"]');
        if (directory) home.querySelector('#gos-brand-directory').open = true;
      });
      home.addEventListener('keydown', event => {
        const needLink = event.target.closest('[data-gos-need-target]');
        if (event.key === ' ' && needTargets.has(needLink) && !event.defaultPrevented &&
            !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          if (!event.repeat) needLink.click();
        }
      });
      needTargets.forEach((panel, link) => {
        link.setAttribute('role', 'button');
        link.setAttribute('aria-pressed', 'false');
        link.setAttribute('aria-controls', panel.id);
      });
      home.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('close', () => {
          document.body.classList.remove('dialog-open');
          if (opener?.isConnected) opener.focus({ preventScroll: true });
        });
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const bounds = dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
        });
      });
      home.querySelectorAll('[data-gos-variant]').forEach(select => {
        select.addEventListener('change', () => {
          const option = select.selectedOptions[0];
          const form = select.form;
          const image = select.closest('dialog').querySelector('[data-gos-variant-image]');
          if (image && option.dataset.imageSrc) {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = option.dataset.imageSrc;
            image.alt = option.dataset.imageAlt || '';
            image.width = Number(option.dataset.imageWidth);
            image.height = Number(option.dataset.imageHeight);
          }
          form.querySelector('[data-gos-price]').textContent = option.dataset.price;
          form.querySelector('[data-gos-add]').disabled = option.dataset.available !== 'true';
          const link = form.querySelector('[data-gos-details]');
          const url = new URL(link.href);
          url.searchParams.set('variant', select.value);
          link.href = url.toString();
        });
      });
      const countryDialog = home.querySelector('.gos-country-dialog');
      const fallback = home.querySelector('.gos-country-fallback');
      if (typeof countryDialog.showModal === 'function') {
        countryDialog.querySelector('[data-gos-country-slot]').append(fallback.querySelector('form'));
        fallback.hidden = true;
      }
      update();
      initializeReveals(home);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
    event.target.querySelectorAll('[data-gos-home]').forEach(home => {
      revealObservers.get(home)?.disconnect();
      revealObservers.delete(home);
    });
  });
})();
